import React, { useEffect, useState } from "react";
import { Youtube, Instagram, Music2, Twitter, Video, Link as LinkIcon, Check, ExternalLink, Loader2, ShieldCheck, RefreshCcw, Trash2, Rss } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const SOURCE_MAP = {
  youtube: { label: "YouTube", icon: Youtube, color: "bg-red-600" },
  instagram: { label: "Instagram", icon: Instagram, color: "bg-pink-600" },
  tiktok: { label: "TikTok", icon: Music2, color: "bg-black border border-white/20" },
  twitter: { label: "X", icon: Twitter, color: "bg-black border border-white/20" },
  vimeo: { label: "Vimeo", icon: Video, color: "bg-sky-600" },
  facebook: { label: "Facebook", icon: Video, color: "bg-blue-700" },
};

export default function SocialImport({ categories = [], onImported }) {
  const [url, setUrl] = useState("");
  const [resolving, setResolving] = useState(false);
  const [meta, setMeta] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("behind_the_scenes");
  const [permission, setPermission] = useState(false);
  const [saving, setSaving] = useState(false);

  const resolveUrl = async () => {
    if (!url.trim()) return;
    setResolving(true);
    setMeta(null);
    try {
      const { data } = await api.post("/external-videos/resolve", { url: url.trim() });
      setMeta(data);
      setTitle(data.title || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResolving(false);
    }
  };

  const doImport = async () => {
    if (!meta) return;
    if (!permission) return toast.error("Please confirm you have permission to re-broadcast.");
    if (!title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const { data } = await api.post("/external-videos/import", {
        url: meta.source_url,
        title: title.trim(),
        description: description.trim(),
        category,
        permission_granted: true,
      });
      toast.success(`Imported from ${meta.source_type}`);
      setUrl(""); setMeta(null); setTitle(""); setDescription(""); setPermission(false);
      onImported?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const src = meta ? SOURCE_MAP[meta.source_type] : null;
  const SourceIcon = src?.icon;
  const isDrmLocked = meta && ["tiktok", "instagram", "twitter", "facebook"].includes(meta.source_type);

  return (
    <div className="space-y-6 max-w-3xl">
      <ChannelSyncPanel onSynced={onImported} />
      <section data-testid="social-import-section" className="tv-glass rounded-2xl border border-white/10 p-6">
      <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold flex items-center gap-2">
        <LinkIcon className="w-4 h-4" /> Import from your socials
      </div>
      <h3 className="font-display font-black text-2xl text-white tracking-tight mt-1">Bring your reels into Tap IN TV</h3>
      <p className="text-white/60 text-sm mt-2">
        Paste a link to your post on YouTube, Instagram, TikTok, X, Vimeo, or Facebook. It plays embedded by default — you can re-host via Cloudflare Stream later for native playback.
      </p>

      <div className="flex items-center gap-2 mt-5 flex-wrap">
        {Object.entries(SOURCE_MAP).map(([k, s]) => {
          const Icon = s.icon;
          return (
            <div key={k} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold text-white ${s.color}`}>
              <Icon className="w-3 h-3" /> {s.label}
            </div>
          );
        })}
      </div>

      <div className="flex gap-2 mt-4">
        <input
          data-testid="social-import-url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && resolveUrl()}
          placeholder="https://www.youtube.com/watch?v=… or https://tiktok.com/@you/video/…"
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-tv-accent"
        />
        <button
          data-testid="social-import-resolve-btn"
          onClick={resolveUrl}
          disabled={resolving || !url.trim()}
          className="bg-white/10 hover:bg-white/20 disabled:opacity-50 text-white font-bold px-5 rounded-lg text-sm flex items-center gap-2"
        >
          {resolving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
          Preview
        </button>
      </div>

      {meta && (
        <div data-testid="social-import-preview" className="mt-6 border border-white/10 rounded-xl overflow-hidden">
          <div className={`flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-bold text-white ${src?.color}`}>
            {SourceIcon && <SourceIcon className="w-4 h-4" />} {src?.label} · detected
          </div>
          <div className="grid md:grid-cols-2 gap-0">
            <div className="bg-black aspect-video">
              <img src={meta.thumbnail_url} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="p-4 space-y-3">
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Title</span>
                <input data-testid="social-import-title" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full mt-1 bg-white/5 border border-white/10 rounded px-3 py-2 text-white text-sm" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Description</span>
                <textarea data-testid="social-import-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full mt-1 bg-white/5 border border-white/10 rounded px-3 py-2 text-white text-sm resize-none" />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Category</span>
                <select data-testid="social-import-category" value={category} onChange={(e) => setCategory(e.target.value)} className="w-full mt-1 bg-white/5 border border-white/10 rounded px-3 py-2 text-white text-sm">
                  {categories.length === 0 ? (
                    <option value="behind_the_scenes">Behind the Scenes</option>
                  ) : (
                    categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)
                  )}
                </select>
              </label>
            </div>
          </div>

          <div className="p-4 border-t border-white/10 bg-white/[0.02]">
            <label data-testid="social-import-permission" className="flex gap-3 items-start cursor-pointer group">
              <input
                type="checkbox"
                checked={permission}
                onChange={(e) => setPermission(e.target.checked)}
                data-testid="social-import-permission-checkbox"
                className="w-5 h-5 mt-0.5 accent-tv-accent shrink-0"
              />
              <div className="text-xs text-white/80 leading-relaxed">
                <div className="flex items-center gap-1.5 font-bold text-white mb-0.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-tv-accent" /> Permission to re-broadcast
                </div>
                I confirm I own this content or have the necessary rights to publish it on Tap IN TV. I understand Tap IN TV will <b>embed</b> this video, display it in rails and on my creator page, and may link back to the original {src?.label} post. I can remove this import at any time.
              </div>
            </label>

            {isDrmLocked && (
              <div className="text-[11px] text-yellow-400/90 bg-yellow-400/10 border border-yellow-400/20 rounded px-3 py-2 mt-3">
                Note: {src?.label} content is DRM-protected — Tap IN TV can only embed it, not re-host via Cloudflare Stream. Your video will always play from {src?.label}.
              </div>
            )}

            <button
              data-testid="social-import-submit-btn"
              onClick={doImport}
              disabled={saving || !permission || !title.trim()}
              className="mt-4 bg-tv-accent hover:bg-yellow-400 disabled:opacity-50 text-black font-bold px-6 py-2.5 rounded-full flex items-center gap-2 text-sm"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {saving ? "Importing…" : `Import from ${src?.label}`}
            </button>
          </div>
        </div>
      )}
    </section>
    </div>
  );
}

function ChannelSyncPanel({ onSynced }) {
  const [feeds, setFeeds] = useState([]);
  const [url, setUrl] = useState("");
  const [permission, setPermission] = useState(false);
  const [busy, setBusy] = useState(false);
  const [syncingId, setSyncingId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/social-feeds/mine"); setFeeds(data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!url.trim()) return;
    if (!permission) return toast.error("Please confirm permission first");
    setBusy(true);
    try {
      await api.post("/social-feeds", { channel_url: url.trim(), permission_granted: true });
      toast.success("Channel linked");
      setUrl(""); setPermission(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const sync = async (id) => {
    setSyncingId(id);
    try {
      const { data } = await api.post(`/social-feeds/${id}/sync`);
      toast.success(data.new_imports > 0 ? `Imported ${data.new_imports} new video${data.new_imports === 1 ? "" : "s"}` : "Up to date — nothing new");
      load();
      onSynced?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncingId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Unlink this channel? Already-imported videos stay in your library.")) return;
    try { await api.delete(`/social-feeds/${id}`); load(); toast.success("Channel unlinked"); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section data-testid="channel-sync-section" className="tv-glass rounded-2xl border border-white/10 p-6">
      <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold flex items-center gap-2">
        <Rss className="w-4 h-4" /> Social Feed Sync
      </div>
      <h3 className="font-display font-black text-2xl text-white tracking-tight mt-1">Auto-import from your channel or playlist</h3>
      <p className="text-white/60 text-sm mt-2">
        Link a YouTube channel, YouTube playlist, or Vimeo channel once. Every time you tap <b className="text-white">Sync now</b> — or on our hourly auto-sync — Tap IN TV pulls the latest videos and adds them as embed-only rows. Re-host the ones you want native from the Library.
      </p>

      <div className="flex gap-2 mt-5">
        <input
          data-testid="channel-sync-url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://youtube.com/@handle, /channel/UC…, /playlist?list=PL…, or vimeo.com/you"
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-tv-accent"
        />
        <button
          data-testid="channel-sync-add-btn"
          onClick={add}
          disabled={busy || !url.trim() || !permission}
          className="bg-tv-accent hover:bg-yellow-400 disabled:opacity-50 text-black font-bold px-5 rounded-lg text-sm flex items-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Link
        </button>
      </div>
      <label className="flex gap-2 items-start mt-3 cursor-pointer">
        <input
          type="checkbox"
          data-testid="channel-sync-permission-checkbox"
          checked={permission}
          onChange={(e) => setPermission(e.target.checked)}
          className="mt-0.5 accent-tv-accent"
        />
        <span className="text-[11px] text-white/70">
          <ShieldCheck className="w-3 h-3 inline text-tv-accent mr-0.5" /> I own or have rights to all content on this channel and grant Tap IN TV permission to embed and display newly-synced videos.
        </span>
      </label>

      {feeds.length > 0 && (
        <div data-testid="channel-sync-list" className="mt-5 space-y-2">
          {feeds.map((f) => {
            const Icon = f.source_type === "vimeo" ? Video : Youtube;
            const synced = f.last_synced_at ? new Date(f.last_synced_at).toLocaleString() : "Never";
            return (
              <div key={f.id} data-testid={`channel-sync-row-${f.id}`} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5">
                <Icon className={`w-5 h-5 ${f.source_type === "youtube" ? "text-red-500" : "text-sky-400"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-sm truncate flex items-center gap-2">
                    {f.handle}
                    {f.kind === "playlist" && (
                      <span className="text-[9px] uppercase tracking-wider bg-tv-accent/20 text-tv-accent px-1.5 py-0.5 rounded font-bold">Playlist</span>
                    )}
                  </div>
                  <div className="text-[11px] text-white/50">Last sync: {synced} · {f.last_synced_count} new</div>
                </div>
                <button
                  data-testid={`channel-sync-btn-${f.id}`}
                  onClick={() => sync(f.id)}
                  disabled={syncingId === f.id}
                  className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-3 py-1.5 rounded-full text-xs flex items-center gap-1 disabled:opacity-50"
                >
                  {syncingId === f.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
                  Sync now
                </button>
                <button onClick={() => remove(f.id)} data-testid={`channel-sync-delete-${f.id}`} className="text-red-400 hover:text-red-300 p-1.5">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
